import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { setActiveTab } from "@actions/commonActions"
import { selectActiveTab } from "@selectors/common"
import { useConversationStore } from "stores/conversationStore"

// MOBILE-PARITY 2026-07-04 (QJ ruling): replaces the compact dropdown (added 2026-07-03,
// itself a replacement of the original bare tab row) with a VISIBLE horizontal tab bar per
// the iPhone app -- teeup-mobile app/components/teeup/chat/index.tsx `tabs` (Chat | All
// activity | Pinned | Mentions | Nudges | All suggestions, underline-active style).
// Presentation-only swap: filter selection/redux wiring (setActiveTab + per-tab unread-dot
// clear) is unchanged from the dropdown it replaces. Title row stays above the tab bar,
// matching the mobile screenshot (PresentationViewHeader title, then TabBar).
interface Tab {
  id: number
  title: string
}

interface Props {
  tabs: Tab[]
}

const ConversationHeader = ({ tabs }: Props) => {
  const dispatch = useDispatch()
  const activeTab = useSelector(selectActiveTab)
  const conversationRedDot = useConversationStore(
    state => state.conversationRedDot
  )
  const setRedDot = useConversationStore(state => state.setRedDot)

  // REVIEW-PACK-FIXES (2026-07-04, QJ defect 1b): at narrow content widths (AI panel open at
  // 1280x800 was the reported case) the tab bar overflows silently -- `overflow-x-auto` already
  // let it scroll, but nothing told the user more tabs existed off-screen, so e.g. "Pinned" /
  // "Mentions" / "Nudges" / "All suggestions" were invisible with zero affordance. Measure real
  // overflow (ResizeObserver so it re-checks live when the AI panel opens/closes or the window
  // resizes) and render edge fade gradients only on the side(s) that actually have more content,
  // never a fixed decoration that would falsely imply hidden tabs when everything fits.
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateFades = () => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 2)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2)
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateFades()
    const observer = new ResizeObserver(updateFades)
    observer.observe(el)
    window.addEventListener("resize", updateFades)
    return () => {
      observer.disconnect()
      window.removeEventListener("resize", updateFades)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.length])

  const handleSelect = (id: number) => {
    dispatch(setActiveTab(id))
    setRedDot(id, false)
  }

  return (
    <div data-testid="conversationHeader" className="border-b border-[#E7E7EA]">
      <div className="px-4 pt-3 pb-2">
        <span className="text-[15px] font-semibold text-[#16181D]">
          Conversation
        </span>
      </div>

      <div className="relative">
        {canScrollLeft && (
          <div
            aria-hidden="true"
            data-testid="conversationTabsFadeLeft"
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-gradient-to-r from-[#FBFDFF] to-transparent"
          />
        )}
        {canScrollRight && (
          <div
            aria-hidden="true"
            data-testid="conversationTabsFadeRight"
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-gradient-to-l from-[#FBFDFF] to-transparent"
          />
        )}
        <div
          ref={scrollRef}
          onScroll={updateFades}
          className="flex items-center gap-5 overflow-x-auto px-4 pb-0"
          style={{ scrollbarWidth: "thin" }}
          role="tablist">
          {tabs.map(({ id, title }) => {
            const isActive = id === activeTab
            return (
              <button
                type="button"
                key={id}
                role="tab"
                aria-selected={isActive}
                data-testid={title + "Tab"}
                onClick={() => handleSelect(id)}
                className={`relative flex shrink-0 items-center gap-1.5 whitespace-nowrap pb-2.5 pt-1 text-[13px] transition-colors ${
                  isActive
                    ? "font-semibold text-[#16181D]"
                    : "font-medium text-[#8B90A0] hover:text-[#586173]"
                }`}>
                {title}
                {conversationRedDot.get(id) && (
                  <span className="h-[6px] w-[6px] rounded-full bg-[rgba(255,0,0,0.54)]" />
                )}
                {isActive && (
                  <span className="absolute inset-x-0 bottom-0 h-[2px] rounded-full bg-[#16181D]" />
                )}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ConversationHeader
